import { useCallback, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import useDopplerBufferState from '../../../../hooks/useDopplerBufferState.js';
import useDopplerFrameTransition from '../../../../hooks/useDopplerFrameTransition.js';
import useDopplerLayerCollection from '../../../../hooks/useDopplerLayerCollection.js';
import useDopplerMapViewState from '../../../../hooks/useDopplerMapViewState.js';

const isFrameIndexMissing = (frameIndex) => frameIndex === null || frameIndex === undefined;

export default function DopplerRadarLayers({
  baseTime,
  frames,
  displayedFrameIndex,
  requestedFrameIndex,
  onDisplayedFrameChange,
  onTransitionStateChange,
  onBufferStateChange,
}) {
  const map = useMap();
  const layersRef = useRef([]);
  const buffer = useDopplerBufferState({ frameCount: frames.length, displayedFrameIndex });
  const frameTransition = useDopplerFrameTransition({
    displayedFrameIndex,
    layersRef,
    onDisplayedFrameChange,
    onTransitionStateChange,
    syncVisibleLayerLoadingState: buffer.syncVisibleLayerLoadingState,
  });

  const syncSettledViewState = useCallback(() => {
    buffer.syncUsableFramesFromLayers(layersRef.current);
    frameTransition.syncVisibleLayerLoadingState();
  }, [buffer.syncUsableFramesFromLayers, frameTransition.syncVisibleLayerLoadingState]);

  const { isMapReady, isMapMoving, isViewStabilizing, clearViewTimeouts } = useDopplerMapViewState({
    map,
    onMovementStart: frameTransition.settleCurrentFrame,
    onViewSettled: syncSettledViewState,
    onViewStable: frameTransition.syncVisibleLayerLoadingState,
  });

  const { resolvedTileSourceMode } = useDopplerLayerCollection({
    baseTime,
    buffer,
    clearViewTimeouts,
    displayedFrameIndex,
    frames,
    frameTransition,
    isMapReady,
    layersRef,
    map,
  });

  useEffect(() => {
    if (
      !isMapReady ||
      !buffer.isPlaybackReady ||
      isFrameIndexMissing(requestedFrameIndex) ||
      !buffer.usableFrames[requestedFrameIndex]
    ) {
      return;
    }

    if (!buffer.hasBeenPrimed && buffer.isFrameLoading(requestedFrameIndex)) {
      return;
    }

    frameTransition.requestFrameTransition(requestedFrameIndex);
  }, [
    buffer.hasBeenPrimed,
    buffer.isFrameLoading,
    buffer.isPlaybackReady,
    buffer.usableFrames,
    frameTransition.requestFrameTransition,
    isMapReady,
    requestedFrameIndex,
  ]);

  const showViewLoadingOverlay =
    !buffer.showLoadingOverlay &&
    (isMapMoving || isViewStabilizing || buffer.isVisibleLayerLoading);
  const viewLoadingReason =
    isMapMoving || isViewStabilizing
      ? 'stabilizing'
      : buffer.isVisibleLayerLoading
        ? 'refining'
        : null;

  useEffect(() => {
    onBufferStateChange?.({
      isPlaybackReady: buffer.isPlaybackReady,
      showLoadingOverlay: buffer.showLoadingOverlay,
      showViewLoadingOverlay,
      tileSourceMode: resolvedTileSourceMode,
      viewLoadingReason,
      startupReadyFrameCount: buffer.startupReadyFrameCount,
      startupTargetFrameCount: buffer.startupTargetFrameCount,
    });
  }, [
    buffer.isPlaybackReady,
    buffer.showLoadingOverlay,
    buffer.startupReadyFrameCount,
    buffer.startupTargetFrameCount,
    onBufferStateChange,
    resolvedTileSourceMode,
    showViewLoadingOverlay,
    viewLoadingReason,
  ]);

  return null;
}
